import { Download } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useAuth } from '@/features/auth/hooks/useAuth'
import DashboardLeadsSection from '@/modules/admin/components/dashboard/DashboardLeadsSection'
import DashboardShimmer from '@/modules/admin/components/dashboard/DashboardShimmer'
import DashboardStats from '@/modules/admin/components/dashboard/DashboardStats'
import OrdersBySalesperson from '@/modules/admin/components/dashboard/OrdersBySalesperson'
import { exportAdminDashboardToExcel } from '@/modules/admin/utils/exportAdminDashboard'
import { Button } from '@/shared/components/ui/button'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'
import { toast } from '@/shared/lib/toast'

import { useAdminDashboardStore } from '../stores/dashboard.store'

export function AdminDashboardPage() {
  const { user } = useAuth()
  const firstName = user?.name?.split(' ')[0] || 'there'

  const [period, setPeriod] = useState('all')
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' })

  const {
    campaignLeads,
    conversionNumbers,
    ordersClosed,
    ordersLost,
    leadsManagement,
    ordersClosedBySalesperson,
    leadsNotFollowedUp,
    target,
    achievement,
    isLoading,
    hasLoaded,
    fetchDashboard,
  } = useAdminDashboardStore()

  useEffect(() => {
    fetchDashboard(period, customRange)

    const handleUpdate = () => {
      fetchDashboard(period, customRange)
    }

    window.addEventListener('focus', handleUpdate)
    window.addEventListener('crm:data-changed', handleUpdate)

    return () => {
      window.removeEventListener('focus', handleUpdate)
      window.removeEventListener('crm:data-changed', handleUpdate)
    }
  }, [fetchDashboard, period, customRange])

  const handleExport = () => {
    try {
      exportAdminDashboardToExcel({
        campaignLeads,
        conversionNumbers,
        ordersClosed,
        ordersLost,
        leadsManagement,
        ordersClosedBySalesperson,
        leadsNotFollowedUp,
        target,
        achievement,
        period,
        customRange,
      })
      toast.success('Dashboard exported to Excel')
    } catch (error) {
      console.error(error)
      toast.error(error?.message || 'Failed to export dashboard')
    }
  }

  // Only full-page shimmer on first load — keeps layout stable when revisiting
  if (isLoading && !hasLoaded) {
    return <DashboardShimmer />
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {period === 'custom' && (
          <CustomDateRangePicker
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            onChange={setCustomRange}
          />
        )}
        <DashboardPeriodFilter value={period} onChange={setPeriod} />
        <Button
          type="button"
          variant="navy"
          size="pill"
          className="h-10 gap-1.5 px-4 text-[13px]"
          onClick={handleExport}
        >
          <Download className="size-3.5" />
          Exports
        </Button>
      </div>

      <div
        className="relative overflow-hidden rounded-2xl px-5 py-5 text-white sm:rounded-[24px] sm:px-8 sm:py-6"
        style={{
          background: `
            repeating-linear-gradient(
              -45deg,
              rgba(255,255,255,0.03) 0px,
              rgba(255,255,255,0.03) 1px,
              transparent 1px,
              transparent 14px
            ),
            linear-gradient(135deg,#0B1220 0%,#111827 55%,#1E293B 100%)
          `,
        }}
      >
        <h1 className="text-[clamp(1.5rem,5vw,2rem)] font-bold tracking-tight">
          Welcome back, {firstName}
        </h1>
        <p className="mt-1 text-sm text-slate-300 sm:text-[15px]">
          Revenue, team movement, receivables, and pipeline control
        </p>
      </div>

      <DashboardStats
        campaignLeads={campaignLeads}
        conversionNumbers={conversionNumbers}
        ordersClosed={ordersClosed}
        ordersLost={ordersLost}
        target={target}
        achievement={achievement}
        ordersClosedBySalesperson={ordersClosedBySalesperson}
        period={period}
        customRange={customRange}
      />

      <DashboardLeadsSection
        leadsManagement={leadsManagement}
        leadsNotFollowedUp={leadsNotFollowedUp}
        period={period}
        customRange={customRange}
      />

      <OrdersBySalesperson
        data={ordersClosedBySalesperson}
        period={period}
        customRange={customRange}
      />
    </div>
  )
}

export default AdminDashboardPage
